import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { LoanCalculator } from '../components/tools/LoanCalculator';
import { 
  DollarSign, Home, Building2, Percent, Calculator, 
  CheckCircle, ArrowRight, ExternalLink, FileText 
} from 'lucide-react';

const loanPrograms = [
  {
    id: 'fha',
    title: 'FHA Loans',
    subtitle: 'Federal Housing Administration',
    description: 'The most popular loan for first-time buyers. As little as 3.5% down, flexible credit requirements, and seller can contribute to closing costs.',
    features: [
      'Down payment as low as 3.5%',
      'Credit scores as low as 580 accepted',
      'Seller can contribute up to 6% of closing costs',
      'Gift funds allowed for down payment',
      '203(k) option combines purchase + renovation',
      'Available for 1-4 unit properties'
    ],
    idealFor: 'First-time homebuyers, buyers with limited savings, those with moderate credit scores',
    icon: Home
  },
  {
    id: 'va',
    title: 'VA Loans',
    subtitle: 'Veterans Affairs Home Loans',
    description: 'Exclusive for eligible veterans and active-duty military. Zero down payment, no PMI, and the best rates available.',
    features: [
      '0% down payment required',
      'No private mortgage insurance (PMI)',
      'Competitive interest rates',
      'Limited closing costs',
      'No prepayment penalties',
      'Available for primary residences'
    ],
    idealFor: 'Veterans, active-duty military, National Guard/Reserve members, eligible surviving spouses',
    icon: FileText
  },
  {
    id: 'conventional',
    title: 'Conventional Loans',
    subtitle: 'Traditional Mortgage Financing',
    description: 'Standard mortgage for buyers with strong credit. Flexible terms, PMI removable at 20% equity, works for primary, second homes, and investment properties.',
    features: [
      'Down payment as low as 3-5%',
      'No upfront mortgage insurance fee',
      'PMI removable at 20% equity',
      'Available for primary, second homes, and investment',
      'Flexible terms: 15, 20, or 30 years',
      'Higher loan limits than FHA'
    ],
    idealFor: 'Buyers with good credit (680+), those with 20%+ down payment, second home buyers',
    icon: Building2
  },
  {
    id: 'dpa',
    title: 'Down Payment Assistance',
    subtitle: 'Programs to Help You Buy',
    description: 'Multiple programs available to help cover your down payment and closing costs. Grants, forgivable loans, and state/local programs for qualified buyers.',
    features: [
      'Grants that don\'t need to be repaid',
      'Forgivable second mortgages',
      'State and local government programs',
      'Income-based qualification',
      'Can be combined with FHA, VA, or Conventional',
      'First-time buyer programs available'
    ],
    idealFor: 'First-time buyers, low-to-moderate income buyers, anyone who needs help with upfront costs',
    icon: DollarSign
  },
  {
    id: 'dscr',
    title: 'DSCR Loans (Investors)',
    subtitle: 'Debt Service Coverage Ratio',
    description: 'Qualify based on property cash flow, not personal income. Built for real estate investors building portfolios.',
    features: [
      'No personal income verification required',
      'Qualify based on property rental income',
      'Available for investment properties only',
      'Close in LLC or corporation name',
      'No limit on number of properties',
      'Interest-only options available'
    ],
    idealFor: 'Real estate investors, self-employed borrowers, portfolio builders',
    icon: Calculator
  }
];

const whyChooseSMF = [
  'Expert guidance through the entire loan process',
  'Access to multiple lenders and programs',
  'Competitive rates and terms',
  'Fast pre-approvals and closings',
  'Local market expertise',
  'Personalized service from application to closing'
];

export const Financing = () => {
  return (
    <div data-testid="financing-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="financing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Simple Mortgage Financing (SMF)</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Financing Solutions <span className="text-gold-gradient">Made Simple</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto">
              Whether you're a first-time buyer, investor, or looking to renovate, we have the right loan program for you. Expert guidance from pre-approval to closing.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
              <Button className="btn-gold" data-testid="financing-consultation-btn">
                Get Pre-Approved <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="https://stan.store/straight4thetop" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="btn-outline-gold" data-testid="financing-resources-btn">
                Financing Resources <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Loan Programs */}
      <section className="py-24 bg-[#111111]" data-testid="loan-programs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Our Programs</p>
            <h2 className="section-title mt-2">Loan Program Breakdown</h2>
          </div>

          <div className="space-y-8">
            {loanPrograms.map((program, index) => (
              <div 
                key={program.id} 
                className="card-hover bg-[#0a0a0a] p-8 rounded-lg"
                data-testid={`loan-program-${program.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Program Header */}
                  <div className="lg:col-span-1">
                    <div className="flex items-center mb-4">
                      <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mr-4">
                        <program.icon className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                          {program.title}
                        </h3>
                        <p className="text-[#D4AF37] text-sm">{program.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-[#808080] leading-relaxed mb-4">{program.description}</p>
                    <div className="p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg">
                      <p className="text-[#D4AF37] text-sm font-medium mb-1">Ideal For:</p>
                      <p className="text-[#F5F5F0] text-sm">{program.idealFor}</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="lg:col-span-2">
                    <h4 className="text-[#F5F5F0] font-medium mb-4">Program Features</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                      {program.features.map((feature, i) => (
                        <div key={i} className="flex items-start text-[#F5F5F0]">
                          <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                      <Button className="btn-gold" data-testid={`preapproved-${program.id}`}>
                        Get Pre-Approved <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="loan-comparison">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Compare Options</p>
            <h2 className="section-title mt-2">Quick Comparison</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-[#111111] rounded-lg overflow-hidden">
              <thead>
                <tr className="border-b border-[#D4AF37]/20">
                  <th className="text-left py-4 px-6 text-[#D4AF37] font-medium">Feature</th>
                  <th className="text-center py-4 px-6 text-[#F5F5F0] font-medium">FHA</th>
                  <th className="text-center py-4 px-6 text-[#F5F5F0] font-medium">VA</th>
                  <th className="text-center py-4 px-6 text-[#F5F5F0] font-medium">Conventional</th>
                  <th className="text-center py-4 px-6 text-[#F5F5F0] font-medium">DPA</th>
                  <th className="text-center py-4 px-6 text-[#F5F5F0] font-medium">DSCR</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#1A1A1A]">
                  <td className="py-4 px-6 text-[#808080]">Min. Down Payment</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">3.5%</td>
                  <td className="py-4 px-6 text-center text-[#D4AF37] font-semibold">0%</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">3-5%</td>
                  <td className="py-4 px-6 text-center text-[#D4AF37] font-semibold">0% (grant)</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">15-25%</td>
                </tr>
                <tr className="border-b border-[#1A1A1A]">
                  <td className="py-4 px-6 text-[#808080]">Income Verification</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Required</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Required</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Required</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Required</td>
                  <td className="py-4 px-6 text-center text-[#D4AF37]">Not Required</td>
                </tr>
                <tr className="border-b border-[#1A1A1A]">
                  <td className="py-4 px-6 text-[#808080]">PMI / MI</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">MIP Required</td>
                  <td className="py-4 px-6 text-center text-[#D4AF37] font-semibold">No PMI</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Removable at 20%</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Varies by program</td>
                  <td className="py-4 px-6 text-center text-[#808080]">Varies</td>
                </tr>
                <tr className="border-b border-[#1A1A1A]">
                  <td className="py-4 px-6 text-[#808080]">Property Type</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Primary</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Primary</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">All Types</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Primary</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Investment Only</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-[#808080]">Best For</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">First-Time Buyers</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Veterans / Military</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Strong Credit</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Need Help w/ Down</td>
                  <td className="py-4 px-6 text-center text-[#F5F5F0]">Investors</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Choose SMF */}
      <section className="py-24 bg-[#111111]" data-testid="why-smf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Why Simple Mortgage Financing</p>
              <h2 className="section-title mt-2 mb-8">Your Partner in Financing</h2>
              <div className="space-y-4">
                {whyChooseSMF.map((item, index) => (
                  <div key={index} className="flex items-center text-[#F5F5F0]">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/buyers">
                  <Button variant="outline" className="btn-outline-gold">
                    <Home className="w-4 h-4 mr-2" /> Buying a Home
                  </Button>
                </Link>
                <Link to="/investors">
                  <Button variant="outline" className="btn-outline-gold">
                    <Building2 className="w-4 h-4 mr-2" /> Investment Properties
                  </Button>
                </Link>
              </div>
            </div>
            <LeadForm 
              source="financing"
              title="Get Pre-Approved Today"
              subtitle="Tell us about your financing needs and we'll match you with the right program."
              buttonText="Start Pre-Approval"
              showPropertyInterest={true}
            />
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="loan-calculator-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="section-overline">Crunch the Numbers</p>
              <h2 className="section-title mt-2 mb-6">Mortgage Calculator</h2>
              <p className="text-[#808080] leading-relaxed mb-6">
                Use our calculator to estimate your monthly mortgage payment. Adjust the home price, down payment, interest rate, and loan term to see how they affect your payment.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 mt-0.5" />
                  <div>
                    <p className="text-[#F5F5F0] font-medium">FHA Loans</p>
                    <p className="text-[#808080] text-sm">Try 3.5% down payment to see FHA options</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 mt-0.5" />
                  <div>
                    <p className="text-[#F5F5F0] font-medium">Conventional Loans</p>
                    <p className="text-[#808080] text-sm">20% down avoids PMI</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 mt-0.5" />
                  <div>
                    <p className="text-[#F5F5F0] font-medium">Investment Properties</p>
                    <p className="text-[#808080] text-sm">DSCR loans typically require 15-25% down</p>
                  </div>
                </div>
              </div>
              <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                <Button className="btn-gold">
                  Get Personalized Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
            <LoanCalculator />
          </div>
        </div>
      </section>
    </div>
  );
};
